import { scopedKeyHelperModule } from '../../helpers/scoped-key/scoped-key';
import { storageServiceModule } from '../../services/storage-service/storage-service';

const REMEMBER_UI_KEY = 'REMEMBERUI';

const SCROLL_EVENT = 'scroll';
const MANUAL_SCROLL_EVENTS =
  'mousedown mouseup wheel mousewheel touchstart touchend';

export const rememberUIComponentModule = angular
  .module('Q.components.remember-ui', [
    storageServiceModule,
    scopedKeyHelperModule,
  ])

  .run(function ($rootScope, $localStorage, $sessionStorage) {
    $rootScope.$on('app.logout', function () {
      // clear all stored settings
      delete $localStorage[REMEMBER_UI_KEY];
      delete $sessionStorage[REMEMBER_UI_KEY];
    });
  })

  /**
   * This service is used to store and retrieve state- or location- based UI information in either
   * localStorage or sessionStorage. For example, it might store which tab was selected,
   * or the scroll position of a window.
   *
   * It's mainly intended to be used with directives to automatically restore the UI when switching
   * routes.
   */
  .factory(
    'RememberUIService',
    function ($rootScope, scopedKey, $localStorage, $sessionStorage) {
      const memory = {};

      memory.perm = $localStorage[REMEMBER_UI_KEY] || {};
      $localStorage[REMEMBER_UI_KEY] = memory.perm;
      memory.session = $sessionStorage[REMEMBER_UI_KEY] || {};
      $sessionStorage[REMEMBER_UI_KEY] = memory.session;

      // re-initialize storage after login or logout
      $rootScope.$on('app.login', () => {
        $localStorage[REMEMBER_UI_KEY] = memory.perm;
        $sessionStorage[REMEMBER_UI_KEY] = memory.session;
      });
      $rootScope.$on('app.logout', () => {
        memory.perm = {};
        memory.session = {};
      });

      return {
        retrieve,
        store,
        forget,
        getOpts,
      };

      //// public functions

      /**
       * Returns the current remembered UI settings
       *
       * Options are used to determine where the value is stored:
       *
       * - id: a unique id for the item
       * - scope: what scope is this stored on?
       *
       * @param opts
       * @returns {*}
       */
      function retrieve(opts) {
        opts = getOpts(opts);
        let val = opts.store[opts.key];
        if (val === undefined && opts.default) {
          val = opts.default;
        }
        return val;
      }

      function store(val, opts) {
        opts = getOpts(opts);
        opts.store[opts.key] = val;
      }

      function forget(opts) {
        opts = getOpts(opts);
        delete opts.store[opts.key];
      }

      //// private functions

      function getOpts(opts) {
        opts = opts || '';

        if (angular.isString(opts)) opts = { id: opts };

        opts = angular.extend(
          {
            id: '',
            scope: 'state',
            session: false,
          },
          opts,
        );

        opts.key = scopedKey(opts.scope);
        opts.key += '(' + opts.id + ')';
        opts.store = opts.session ? memory.session : memory.perm;

        return opts;
      }
    },
  )

  .factory(
    'RememberUIWindowScroll',
    function (
      RememberUIService,
      $rootScope,
      $window,
      $timeout,
      $state,
      scopedKey,
    ) {
      const opts = {
        id: 'WINDOWSCROLL',
        default: 0,
        scope() {
          // allow a route to override the scoped key for scrolling
          const rememberScrollKey =
            $state.current && $state.current.data.rememberScrollKey;
          return rememberScrollKey
            ? scopedKey.activeState(rememberScrollKey)
            : scopedKey.activeState('url');
        },
        session: true,
      };

      let initialized = false;
      let active = false;

      function enable() {
        active = true;

        if (!initialized) {
          const init = RememberUIService.retrieve(opts);
          const $windowEl = angular.element($window);
          let ignoreChanges = true;
          // track pop state to ensure we only restore scroll on back/forward or reload
          // defaults to true to handle page reloading
          let didPopState = true;

          const storeScrollPos = () => {
            if (active && !ignoreChanges) {
              $rootScope.$applyAsync(() =>
                RememberUIService.store($windowEl.scrollTop(), opts),
              );
            }
          };

          const setScrollPos = (keepIgnoring) => {
            //noinspection JSUnusedAssignment
            ignoreChanges = true;
            $windowEl.scrollTop(
              didPopState ? RememberUIService.retrieve(opts) : 0,
            );
            ignoreChanges = !!keepIgnoring;
            if (!didPopState) {
              storeScrollPos();
            }
          };

          if (init !== undefined) {
            setScrollPos(true);
            $timeout(setScrollPos, 50);
          }

          $windowEl.on(SCROLL_EVENT, storeScrollPos);

          $rootScope.$on('$stateChangeStart', () => {
            if (active) {
              ignoreChanges = true;
              $windowEl.scrollTop(0);
            }
          });
          $windowEl.on('popstate', () => {
            if (active) {
              didPopState = true;
            }
          });
          $rootScope.$on('$stateChangeSuccess', () => {
            if (active && !$rootScope.stateHelperRedirecting) {
              $timeout(() => {
                setScrollPos();
                didPopState = false;
              }, 100);
            }
          });

          ignoreChanges = false;

          initialized = true;
        }
      }

      function disable() {
        active = false;
      }

      function forget() {
        RememberUIService.forget(opts);
      }

      return {
        enable,
        disable,
        forget,
      };
    },
  )

  /**
   * Provides a controller for other directives to latch onto for remembering the UI configuration.
   *
   * It is recommended that you provide at least an ID attribute on the node, or pass an ID into the `remember-ui`
   * attribute.
   *
   * Usage: <any remember-ui="{options}|'id'">
   *
   * Options:
   *
   * - if nothing is passed in, the node's ID attribute will be used as the storage id, and if that fails,
   *   the node's nodeType will be used instead (not recommended).
   *
   * - if just passed in a string, use that for id, and use the default options:
   *     - scope: 'state'
   *     - session: false
   *
   * - if passed in an object, this will be used to determine the following settings:
   *     - id: unique ID for the node
   *     - scope: what scope is this stored on?
   *          'state' = state id (default)
   *          Array = use state id followed by specified $stateParams
   *          'url' = full url without the hash
   *          function = return a string of your choosing, options are passed back in
   *          anything else = converted to string, used as is
   *     - session: set to true to only store this value for the given session. By default, all settings
   *          are put on localStorage, and only cleared on app logout.
   */
  .directive('rememberUi', function () {
    return {
      // make sure it runs before normal directives
      priority: 100,
      restrict: 'A',
      controller: 'RememberUICtrl',
      compile() {
        return {
          pre(scope, elem, attrs, ctrl) {
            ctrl.init(elem, attrs, scope.$eval(attrs.rememberUi));
          },
        };
      },
    };
  })

  /**
   * Directive that enables storing arbitrary scope data in a RememberUI key.
   */
  .directive('rememberUiData', function ($parse) {
    return {
      restrict: 'A',
      require: 'rememberUi',
      link(scope, elem, attr, rememberUiCtrl) {
        let scopeProperty = attr.rememberUiData || scope.$eval(attr.rememberUi);
        let oldVal = rememberUiCtrl.retrieve();
        if (oldVal) {
          let v = $parse(scopeProperty);
          v.assign(scope, oldVal);
        }
        scope.$watch(scopeProperty, function (newData) {
          rememberUiCtrl.store(newData);
        });
      },
    };
  })

  /**
   * Directive to remember the scroll position of a node.
   *
   * Unlike the default remember-ui directive, this one changes two defaults:
   *
   * - scope is 'url': this means that scrolling is remembered for the _exact_ URL by default
   * - session is true: we forget scroll position if the user leaves the page and comes back later, much like
   *     the browser's default scroll memory
   *
   */
  .directive(
    'rememberUiScroll',
    function ($timeout, $state, scopedKey, $window, $rootScope) {
      return {
        restrict: 'A',
        controller: 'RememberUICtrl',
        compile() {
          return {
            pre(scope, elem, attrs, ctrl) {
              let opts = scope.$eval(attrs.rememberUiScroll);
              if (!opts || angular.isString(opts)) {
                opts = { id: opts };
              }

              opts = angular.extend(
                {
                  default: 0,
                  scope() {
                    // allow a route to override the scoped key for scrolling
                    const rememberScrollKey =
                      $state.current && $state.current.data.rememberScrollKey;
                    return rememberScrollKey
                      ? scopedKey.activeState(rememberScrollKey)
                      : scopedKey.activeState('url');
                  },
                  session: true,
                },
                opts,
              );

              ctrl.init(elem, attrs, opts);
            },

            post(scope, elem, attrs, ctrl) {
              const init = ctrl.retrieve();
              let lastKey = null;
              let ignoreChanges = true;
              // track pop state to ensure we only restore scroll on back/forward or reload
              // defaults to true to handle page reloading
              let didPopState = true;
              let scrolledWhileLoading = false;

              const storeScrollPos = () => {
                if (!ignoreChanges) {
                  ctrl.store(elem.scrollTop());
                }
              };

              const loadingScrollListener = () => {
                if (ignoreChanges && scrolledWhileLoading !== true) {
                  const top = elem.scrollTop();
                  const maxTop = top - elem.offsetHeight();
                  if (
                    top !== scrolledWhileLoading &&
                    scrolledWhileLoading <= maxTop
                  ) {
                    scrolledWhileLoading = true;
                  }
                }
              };

              const restoreScrollPos = (keepIgnoring) => {
                ignoreChanges = true;
                elem[0].scrollTop = didPopState ? ctrl.retrieve() : 0;
                ignoreChanges = !!keepIgnoring;
                if (!didPopState) {
                  storeScrollPos();
                }
              };

              if (init !== undefined) {
                restoreScrollPos(true);
                $timeout(restoreScrollPos, 50);
              }

              elem.on(SCROLL_EVENT, storeScrollPos);
              elem.on(MANUAL_SCROLL_EVENTS, loadingScrollListener);
              scope.$on('$stateChangeStart', () => {
                if (lastKey === ctrl.getOpts().scope) return;
                ignoreChanges = true;
                scrolledWhileLoading = elem[0].scrollTop;
                $timeout(
                  () => {
                    if (scrolledWhileLoading !== true) {
                      scrolledWhileLoading = elem[0].scrollTop = didPopState
                        ? ctrl.retrieve()
                        : 0;
                    }
                  },
                  1,
                  false,
                );
              });
              const windowPopStateListener = () => {
                didPopState = true;
              };
              angular.element($window).on('popstate', windowPopStateListener);
              scope.$on('$stateChangeSuccess', () => {
                if (!$rootScope.stateHelperRedirecting) {
                  const key = scopedKey.activeState(ctrl.getOpts().scope);
                  if (lastKey === key) return;
                  lastKey = key;
                  $timeout(
                    () => {
                      if (scrolledWhileLoading === true) {
                        ignoreChanges = false;
                        storeScrollPos();
                      } else {
                        restoreScrollPos();
                      }
                      scrolledWhileLoading = false;
                      didPopState = false;
                    },
                    100,
                    false,
                  );
                }
              });

              ignoreChanges = false;

              scope.$on('$destroy', function () {
                elem.off(SCROLL_EVENT, storeScrollPos);
                elem.off(MANUAL_SCROLL_EVENTS, loadingScrollListener);
                angular
                  .element($window)
                  .off('popstate', windowPopStateListener);
              });
            },
          };
        },
      };
    },
  )

  .directive('disableUiScroll', function (RememberUIWindowScroll) {
    return {
      restrict: 'A',
      require: '^?rememberUiScroll',
      link(scope, elem, attr, rememberUiScrollCtrl) {
        const forget = (val) => {
          if (val === 'true') {
            RememberUIWindowScroll.forget();
            if (rememberUiScrollCtrl) {
              rememberUiScrollCtrl.forget();
            }
          }
        };
        attr.$observe('disableUiScroll', forget);
        forget(attr.disableUiScroll);
      },
    };
  })

  /**
   * Shared controller for remember-ui and remember-ui-scroll
   */
  .controller('RememberUICtrl', function (RememberUIService) {
    const ctrl = this;
    let _opts;

    ctrl.init = function (elem, attr, opts) {
      if (opts === false) return;
      _opts = opts || {};
      if (angular.isString(opts)) {
        _opts = { id: opts };
      }
      _opts.id = _opts.id || attr.id || elem[0].nodeName.toLowerCase();
    };

    ctrl.retrieve = function () {
      return _opts && RememberUIService.retrieve(_opts);
    };

    ctrl.store = function (val) {
      return _opts && RememberUIService.store(val, _opts);
    };

    ctrl.forget = function () {
      return _opts && RememberUIService.forget(_opts);
    };
    ctrl.getOpts = function () {
      return _opts && RememberUIService.getOpts(_opts);
    };
  });
